// baseLimitFeedback.js

import { parseBaseLimitError } from 'utilities/BaseLimitErrors';

const OPEN_BASE_PLAN_EVENT = 'orbital:open-base-plan';

const listeners = new Set();

function normalizeFirebaseError(error) {
  if (!error || typeof error !== 'object') {
    return error;
  }

  const detailsCandidates = [error.details, error.customData, error.customData?.details];
  const details = detailsCandidates.find((value) => (
    value && typeof value === 'object' && !Array.isArray(value)
    && (value.code || value.limitType)
  )) || {};

  if (details.code || details.limitType) {
    return {
      ...error,
      details,
      message: String(error.message || '').trim() || details.message || 'Base limit reached.',
    };
  }

  return error;
}

function mapLiveErrorCode(code) {
  const normalized = String(code || '').trim().toLowerCase();
  if (!normalized.startsWith('live_')) {
    return null;
  }

  return {
    code: 'base_limit_reached',
    limitType: 'live',
  };
}

/**
 * Opens Base Settings → Plan.
 *
 * @param {string|object} [planKeyOrOptions] - Optional plan key, or `{ planKey }`.
 */
export function openBasePlanSettings(planKeyOrOptions = null) {
  if (typeof window === 'undefined') return;

  let planKey = '';
  if (typeof planKeyOrOptions === 'string') {
    planKey = planKeyOrOptions.trim();
  } else if (planKeyOrOptions && typeof planKeyOrOptions === 'object') {
    planKey = String(planKeyOrOptions.planKey || '').trim();
  }

  window.dispatchEvent(new CustomEvent(OPEN_BASE_PLAN_EVENT, {
    detail: {
      tab: 'plan',
      ...(planKey ? { planKey } : {}),
    },
  }));
}

export function subscribeOpenBasePlanSettings(handler) {
  if (typeof window === 'undefined' || typeof handler !== 'function') {
    return () => {};
  }

  const listener = (event) => {
    handler(event?.detail || {});
  };

  window.addEventListener(OPEN_BASE_PLAN_EVENT, listener);
  return () => {
    window.removeEventListener(OPEN_BASE_PLAN_EVENT, listener);
  };
}

export function subscribeBaseLimitFeedback(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }

  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifyBaseLimitError({
  error,
  baseKey,
  fallbackMessage = '',
} = {}) {
  const normalizedError = normalizeFirebaseError(error);
  let parsed = parseBaseLimitError(normalizedError);

  if (!parsed) {
    const message = String(normalizedError?.message || fallbackMessage || '').trim();
    const codeMatch = message.match(/\((live_[a-z0-9_]+)\)\s*$/i);
    const liveMapping = mapLiveErrorCode(codeMatch?.[1]);
    if (liveMapping) {
      parsed = parseBaseLimitError({
        message: message.replace(/\s*\(live_[a-z0-9_]+\)\s*$/i, '').trim(),
        details: liveMapping,
      });
    }
  }

  if (!parsed) {
    return false;
  }

  listeners.forEach((listener) => {
    listener({
      error: parsed,
      baseKey: String(baseKey || '').trim() || null,
    });
  });

  return true;
}

export async function runWithBaseLimitFeedback(baseKey, task) {
  try {
    return await task();
  } catch (error) {
    notifyBaseLimitError({ error, baseKey });
    throw error;
  }
}
